import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { RoleBadge } from '../components/RoleBadge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import api from '../utils/api';
import { toast } from 'sonner';
import { UserPlus, Edit, Power } from 'lucide-react';

const roles = [
  'Rep',
  'District_Leader',
  'Division_Leader',
  'Regional_Leader',
  'Senior_Regional_Leader',
  'Regional_Vice_President',
  'Admin'
];

const roleLabels = {
  'Rep': 'Representative',
  'District_Leader': 'District Leader',
  'Division_Leader': 'Division Leader',
  'Regional_Leader': 'Regional Leader',
  'Senior_Regional_Leader': 'Senior Regional Leader',
  'Regional_Vice_President': 'Regional Vice President',
  'Admin': 'Admin'
};

export const RoleManagement = () => {
  const [users, setUsers] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'Rep'
  });
  const [roleUpdate, setRoleUpdate] = useState({
    role: '',
    note: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/users', newUser);
      toast.success('User created successfully!');
      setShowCreateModal(false);
      setNewUser({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        role: 'Rep'
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditModal = (user) => {
    setSelectedUser(user);
    setRoleUpdate({
      role: user.role,
      note: ''
    });
    setShowEditModal(true);
  };

  const handleUpdateRole = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.put(`/users/${selectedUser.id}/role`, roleUpdate);
      toast.success('Role updated successfully!');
      setShowEditModal(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      const response = await api.put(`/users/${userId}/deactivate`);
      toast.success(`User ${response.data.is_active ? 'activated' : 'deactivated'} successfully!`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  return (
    <div className="space-y-6" data-testid="role-management-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-[#333333]">Role & Promotion Management</h1>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-[#E31837] hover:bg-[#C4122C] flex items-center gap-2"
          data-testid="create-user-button"
        >
          <UserPlus className="h-4 w-4" />
          Create New User
        </Button>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Current Role</TableHead>
                <TableHead>Account Status</TableHead>
                <TableHead>Created At</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                  <TableCell className="font-medium">
                    {user.first_name} {user.last_name}
                  </TableCell>
                  <TableCell className="text-gray-600">{user.email}</TableCell>
                  <TableCell>
                    <RoleBadge role={user.role} />
                  </TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                        user.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                      data-testid={`user-status-${user.id}`}
                    >
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </TableCell>
                  <TableCell className="text-gray-600">
                    {new Date(user.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenEditModal(user)}
                        data-testid={`edit-role-${user.id}`}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit Role
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggleActive(user.id, user.is_active)}
                        data-testid={`toggle-active-${user.id}`}
                      >
                        <Power className="h-4 w-4 mr-1" />
                        {user.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Create User Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent data-testid="create-user-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#E31837]">Create New User</DialogTitle>
            <DialogDescription>Add a new team member to the system</DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  value={newUser.first_name}
                  onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                  required
                  data-testid="create-first-name-input"
                />
              </div>
              <div>
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  value={newUser.last_name}
                  onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                  required
                  data-testid="create-last-name-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                required
                data-testid="create-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password">Temporary Password *</Label>
              <Input
                id="password"
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                required
                data-testid="create-password-input"
              />
            </div>

            <div>
              <Label htmlFor="role">Starting Role *</Label>
              <Select
                value={newUser.role}
                onValueChange={(value) => setNewUser({ ...newUser, role: value })}
              >
                <SelectTrigger data-testid="create-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.map(role => (
                    <SelectItem key={role} value={role}>{roleLabels[role]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="flex-1"
                data-testid="cancel-create-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#E31837] hover:bg-[#C4122C]"
                disabled={loading}
                data-testid="submit-create-button"
              >
                {loading ? 'Creating...' : 'Create User'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Role Modal */}
      {selectedUser && (
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent data-testid="edit-role-modal" className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-[#E31837]">Update Role</DialogTitle>
              <DialogDescription>
                Update role for {selectedUser.first_name} {selectedUser.last_name}
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleUpdateRole} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="new_role">New Role *</Label>
                <Select
                  value={roleUpdate.role}
                  onValueChange={(value) => setRoleUpdate({ ...roleUpdate, role: value })}
                >
                  <SelectTrigger data-testid="edit-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map(role => (
                      <SelectItem key={role} value={role}>{roleLabels[role]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="note">Note</Label>
                <Textarea
                  id="note"
                  placeholder="Reason for promotion/demotion..."
                  value={roleUpdate.note}
                  onChange={(e) => setRoleUpdate({ ...roleUpdate, note: e.target.value })}
                  rows={3}
                  data-testid="edit-role-note-input"
                />
              </div>

              {selectedUser.promotion_history && selectedUser.promotion_history.length > 0 && (
                <div>
                  <Label>Promotion History</Label>
                  <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                    {selectedUser.promotion_history.map((entry, idx) => (
                      <div key={idx} className="text-sm p-2 bg-gray-50 rounded">
                        <div className="font-semibold">{roleLabels[entry.role] || entry.role}</div>
                        <div className="text-gray-600 text-xs">
                          {new Date(entry.date).toLocaleDateString()} - {entry.note}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1"
                  data-testid="cancel-edit-role-button"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#E31837] hover:bg-[#C4122C]"
                  disabled={loading}
                  data-testid="submit-edit-role-button"
                >
                  {loading ? 'Updating...' : 'Update Role'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
